import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { ArrowLeftRight, ArrowRight } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { EditStockMovementDialog } from '@/components/inventory/EditStockMovementDialog';
import { StockMovementDialog } from '@/components/inventory/StockMovementDialog';
import {
  ADJUSTMENT_REASON_LABELS,
  STOCK_MOVEMENT_TYPE_LABELS,
  STOCK_MOVEMENT_TYPES,
  useStockMovements,
  type AdjustmentReason,
  type StockMovementRow,
  type StockMovementType,
} from '@/features/inventory/hooks';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatDateTime } from '@/lib/formatters';

const TYPE_VARIANT: Record<StockMovementType, NonNullable<BadgeProps['variant']>> = {
  receipt: 'success',
  issue: 'warning',
  transfer: 'info',
  adjustment: 'secondary',
  cycle_count: 'secondary',
  production_consume: 'muted',
  production_produce: 'muted',
};

const TYPE_OPTIONS = STOCK_MOVEMENT_TYPES.map((t) => ({
  value: t,
  label: STOCK_MOVEMENT_TYPE_LABELS[t],
}));

function reasonLabel(code: string | null): string | null {
  if (!code) return null;
  if (code === 'reversal') return 'Reversal';
  return ADJUSTMENT_REASON_LABELS[code as AdjustmentReason] ?? code;
}

/**
 * Stock-movement ledger. Rows stay in the log when corrected: the original
 * is marked Reversed and a new movement adjusts on-hand. Open a row to
 * change quantity, item, or location, or to reverse it.
 */
export function StockMovementsPage() {
  const { role } = useAuth();
  const { data, isLoading } = useStockMovements({ limit: 500 });
  const [movementOpen, setMovementOpen] = useState(false);
  const [editing, setEditing] = useState<StockMovementRow | null>(null);

  const canManage = canWrite(role, 'inventory');
  const rows = useMemo<StockMovementRow[]>(() => data ?? [], [data]);

  const columns = useMemo<ColumnDef<StockMovementRow>[]>(
    () => [
      {
        id: 'created_at',
        accessorKey: 'created_at',
        header: 'When',
        size: 160,
        minSize: 120,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: 'Type',
        size: 130,
        minSize: 100,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const t = row.original.type;
          return (
            <Badge variant={TYPE_VARIANT[t] ?? 'secondary'}>
              {STOCK_MOVEMENT_TYPE_LABELS[t] ?? t}
            </Badge>
          );
        },
      },
      {
        id: 'sku',
        accessorFn: (r) => r.items?.sku ?? '',
        header: 'SKU',
        size: 140,
        minSize: 90,
        cell: ({ row }) => (
          <Link
            to={`/catalog/items/${row.original.item_id}?tab=inventory`}
            onClick={(e) => e.stopPropagation()}
            className="whitespace-nowrap font-mono text-xs text-foreground hover:underline"
          >
            {row.original.items?.sku ?? '—'}
          </Link>
        ),
      },
      {
        id: 'name',
        accessorFn: (r) => r.items?.name ?? '',
        header: 'Item',
        size: 220,
        minSize: 140,
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{row.original.items?.name ?? '—'}</span>
        ),
      },
      {
        id: 'movement',
        accessorFn: (r) =>
          `${r.from?.name ?? r.from?.code ?? ''} ${r.to?.name ?? r.to?.code ?? ''}`.trim(),
        header: 'Movement',
        size: 220,
        minSize: 150,
        enableSorting: false,
        cell: ({ row }) => {
          const { from, to } = row.original;
          return (
            <span className="flex items-center gap-1.5 text-sm">
              <span className={from ? 'text-foreground' : 'text-muted-foreground'}>
                {from?.name ?? '—'}
              </span>
              <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className={to ? 'text-foreground' : 'text-muted-foreground'}>
                {to?.name ?? '—'}
              </span>
            </span>
          );
        },
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: () => <span className="block text-right">Qty</span>,
        size: 100,
        minSize: 80,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">
            {Number(row.original.quantity).toLocaleString()}
            <span className="ml-1 text-xs text-muted-foreground">
              {row.original.items?.base_uom}
            </span>
          </span>
        ),
      },
      {
        id: 'lot',
        accessorFn: (r) => r.lots?.lot_number ?? '',
        header: 'Lot',
        size: 120,
        minSize: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.lots?.lot_number ?? '—'}
          </span>
        ),
      },
      {
        id: 'status',
        accessorFn: (r) =>
          r.reverses_movement_id ? 'reversal' : r.status === 'reversed' ? 'reversed' : 'posted',
        header: 'Status',
        size: 120,
        minSize: 90,
        cell: ({ row }) => {
          if (row.original.reverses_movement_id) return <Badge variant="outline">Reversal</Badge>;
          if (row.original.status === 'reversed') return <Badge variant="muted">Reversed</Badge>;
          return <span className="text-muted-foreground">Posted</span>;
        },
      },
      {
        id: 'detail',
        accessorFn: (r) => [reasonLabel(r.reason_code), r.memo].filter(Boolean).join(' — '),
        header: 'Reason / memo',
        size: 240,
        minSize: 140,
        enableSorting: false,
        cell: ({ row }) => {
          const reason = reasonLabel(row.original.reason_code);
          const memo = row.original.memo;
          if (!reason && !memo) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="flex flex-wrap items-center gap-1.5">
              {reason && <Badge variant="outline">{reason}</Badge>}
              {memo && (
                <span className="text-muted-foreground" title={memo}>
                  {memo}
                </span>
              )}
            </span>
          );
        },
      },
    ],
    [],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [{ columnId: 'type', title: 'Type', options: TYPE_OPTIONS }],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Stock movements"
        actions={
          canManage ? (
            <Button onClick={() => setMovementOpen(true)}>
              <ArrowLeftRight className="h-4 w-4" />
              Record movement
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardContent className="pt-6">
          <DataTable<StockMovementRow, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={canManage ? setEditing : undefined}
            rowClassName={(r) => (r.status === 'reversed' ? 'opacity-60' : undefined)}
            searchPlaceholder="Search by SKU, item, location, or memo…"
            facetedFilters={facetedFilters}
            initialPageSize={50}
            pageSizes={[25, 50, 100, 200]}
            tableId="inventory-movements"
            mobileCard={{
              title: (row) => row.items?.name ?? '—',
              subtitle: (row) => <span className="font-mono text-[11px]">{row.items?.sku}</span>,
              badges: (row) => [
                <Badge key="type" variant={TYPE_VARIANT[row.type] ?? 'secondary'}>
                  {STOCK_MOVEMENT_TYPE_LABELS[row.type] ?? row.type}
                </Badge>,
                row.status === 'reversed' ? (
                  <Badge key="status" variant="muted">
                    Reversed
                  </Badge>
                ) : row.reverses_movement_id ? (
                  <Badge key="status" variant="outline">
                    Reversal
                  </Badge>
                ) : null,
                reasonLabel(row.reason_code) ? (
                  <Badge key="reason" variant="outline">
                    {reasonLabel(row.reason_code)}
                  </Badge>
                ) : null,
              ],
              amount: (row) => ({
                value: `${Number(row.quantity).toLocaleString()} ${row.items?.base_uom ?? ''}`,
                tone: 'neutral',
              }),
              meta: (row) => {
                const m: { label: string; value: React.ReactNode }[] = [
                  {
                    label: 'Movement',
                    value: `${row.from?.name ?? '—'} → ${row.to?.name ?? '—'}`,
                  },
                  { label: 'When', value: formatDateTime(row.created_at) },
                ];
                if (row.lots?.lot_number) m.push({ label: 'Lot', value: row.lots.lot_number });
                return m;
              },
            }}
            emptyState={
              <EmptyState
                icon={ArrowLeftRight}
                title="No movements yet"
                action={
                  canManage ? (
                    <Button onClick={() => setMovementOpen(true)}>
                      <ArrowLeftRight className="h-4 w-4" />
                      Record movement
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      {canManage && (
        <>
          <StockMovementDialog open={movementOpen} onOpenChange={setMovementOpen} />
          <EditStockMovementDialog
            open={editing !== null}
            onOpenChange={(o) => !o && setEditing(null)}
            movement={editing}
          />
        </>
      )}
    </div>
  );
}
